import type { PreviewLayer } from '@/components/preview/preview-static-layer-renderer';
import { parseImageSequenceUrls } from '@/components/preview/preview-presentation-slides-layer.helpers';
import { isPreviewLayersArray } from '@/helpers/preview/preview-layer-utils';

type MediaPreviewPayload = {
  id?: string;
  fileType?: string;
  url?: string;
  assetVersion?: number;
  processingStatus?: string;
  slideCount?: number;
  frameCount?: number;
  frameSlideNumbers?: number[];
  slidePreviewUrls?: string[];
  imageUrls?: string[];
  videoOverlays?: string;
};

const SEQUENCE_FILE_TYPES = new Set(['presentation', 'pdf', 'imageSequence']);

function mediaFromSyncData(playerSyncData: Record<string, unknown>): MediaPreviewPayload | undefined {
  return (playerSyncData.payload as { media?: MediaPreviewPayload } | undefined)?.media;
}

function previewFrameUrls(media: MediaPreviewPayload): string[] {
  const preferred = media.slidePreviewUrls?.length ? media.slidePreviewUrls : media.imageUrls;
  const urls = (preferred ?? []).map((url) => (typeof url === 'string' ? url : ''));
  return urls.some((url) => url.length > 0) ? urls : [];
}

function sequenceLayerData(media: MediaPreviewPayload, urls: string[]): Record<string, string> {
  const urlsJson = JSON.stringify(urls);
  const frameCount = media.frameCount ?? media.slideCount ?? urls.length;
  const data: Record<string, string> = {
    preparing: 'false',
    imageUrls: urlsJson,
    slidePreviewUrls: urlsJson,
    frameCount: String(frameCount),
    slideCount: String(frameCount),
  };
  if (media.fileType) {
    data.fileType = media.fileType;
  }
  if (media.assetVersion != null) {
    data.assetVersion = String(media.assetVersion);
  }
  if (media.frameSlideNumbers?.length) {
    data.frameSlideNumbers = JSON.stringify(media.frameSlideNumbers);
  }
  if (media.videoOverlays) {
    data.videoOverlays = media.videoOverlays;
  }
  if (media.processingStatus === 'rejected') {
    data.rejected = 'true';
  }
  return data;
}

/** One full-bleed layer the preview modal can paint when control-preview omitted layers. */
export function previewLayerFromMediaPayload(media: MediaPreviewPayload): PreviewLayer | undefined {
  const itemId = media.id ? `media:${media.id}` : undefined;
  const urls = previewFrameUrls(media);
  const base = {
    layerId: 300,
    position: { x: 0, y: 0, w: 100, h: 100 },
    visible: true,
    itemId,
  };

  if (urls.length > 0 && (SEQUENCE_FILE_TYPES.has(media.fileType ?? '') || !media.url)) {
    return { ...base, componentId: 'imageSequence', data: sequenceLayerData(media, urls) };
  }

  if (!media.url) {
    return undefined;
  }

  if (media.fileType === 'image') {
    return { ...base, componentId: 'image', data: { src: media.url } };
  }
  if (media.fileType === 'audio') {
    return { ...base, componentId: 'audio', data: { audioUrl: media.url } };
  }
  if (media.fileType === 'video' || !SEQUENCE_FILE_TYPES.has(media.fileType ?? '')) {
    return { ...base, componentId: 'video', data: { videoUrl: media.url } };
  }

  return undefined;
}

function sequenceLayerHasUrl(layer: PreviewLayer): boolean {
  return parseImageSequenceUrls(layer.data).some((url) => url.length > 0);
}

function layerPaintsMedia(layer: PreviewLayer): boolean {
  if (layer.componentId === 'imageSequence' || layer.componentId === 'presentationSlides') {
    return sequenceLayerHasUrl(layer);
  }
  if (layer.componentId === 'image') {
    return Boolean(layer.data.src || layer.data.imageUrl || layer.data.url);
  }
  if (layer.componentId === 'video' || layer.componentId === 'backgroundVideo') {
    return Boolean(layer.data.videoUrl || layer.data.url);
  }
  if (layer.componentId === 'audio') {
    return Boolean(layer.data.audioUrl || layer.data.url);
  }
  if (layer.componentId === 'pdf') {
    return Boolean(layer.data.pdfUrl || layer.data.url);
  }
  return false;
}

function syncDataFromPreviewResponse(playerSyncData: Record<string, unknown>): Record<string, unknown> {
  const nested = playerSyncData.playerSyncData;
  if (nested && typeof nested === 'object' && !playerSyncData.page && !playerSyncData.payload) {
    return nested as Record<string, unknown>;
  }
  return playerSyncData;
}

/**
 * control-preview sometimes returns slide URLs only on payload.media (no layers).
 * The modal fetches images from layer data, so copy or synthesize that layer here.
 * Event-management responses already include layers; contributor responses often do not.
 */
export function enrichMediaPreviewLayersFromPayload(playerSyncData: Record<string, unknown>): Record<string, unknown> {
  const syncData = syncDataFromPreviewResponse(playerSyncData);
  const media = mediaFromSyncData(syncData);
  const hasFrameUrls = Boolean(media && previewFrameUrls(media).length > 0);
  if (syncData.page && syncData.page !== 'media' && !hasFrameUrls) {
    return syncData;
  }

  const layers = syncData.layers;

  if (isPreviewLayersArray(layers) && media && hasFrameUrls) {
    const needsUrls = (layer: PreviewLayer) =>
      (layer.componentId === 'imageSequence' || layer.componentId === 'presentationSlides') &&
      !sequenceLayerHasUrl(layer);
    // In a composition, only the sequence layer for this media item may take its slides.
    const target =
      layers.length === 1
        ? layers.find(needsUrls)
        : layers.find((layer) => needsUrls(layer) && media.id != null && layer.itemId === `media:${media.id}`);
    if (target) {
      const urls = previewFrameUrls(media);
      return {
        ...syncData,
        layers: layers.map((layer) =>
          layer === target ? { ...layer, data: { ...layer.data, ...sequenceLayerData(media, urls) } } : layer,
        ),
      };
    }
  }

  if (isPreviewLayersArray(layers) && layers.some(layerPaintsMedia)) {
    return syncData;
  }

  if (!media) {
    return syncData;
  }

  const synthesized = previewLayerFromMediaPayload(media);
  if (!synthesized) {
    return syncData;
  }

  return { ...syncData, layers: [synthesized] };
}
